import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Users, Layers, MapPin, ShieldCheck, Weight, ShoppingCart,
  CalendarCheck, ClipboardList, AlertTriangle, UserX, Ban, Clock, TrendingUp,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from "recharts";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import FavoritesRow from "@/components/FavoritesRow";

const MANAGER_ROLES = ["ASM", "RSM", "ZSM", "SALES_HEAD", "MANAGEMENT", "SUPER_ADMIN"];

const KG = (n) => `${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 })} kg`;

function Stat({ icon: Icon, label, value, sub, tint, testid, onClick }) {
  return (
    <Card
      data-testid={testid}
      onClick={onClick}
      className={"border-neutral-200 rounded-2xl overflow-hidden hover:shadow-md hover:shadow-[#6D2C91]/5 transition-shadow "
        + (onClick ? "cursor-pointer" : "")}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1 truncate">{label}</div>
            <div className="text-3xl font-extrabold text-neutral-900 tracking-tight">{value}</div>
            {sub && <div className="text-[11px] text-neutral-400 mt-0.5">{sub}</div>}
          </div>
          <div
            className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: tint }}
          >
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SectionCard({ title, action, children, testid }) {
  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid={testid}>
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-semibold text-neutral-800">{title}</CardTitle>
        {action}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

const EMPTY_FILTERS = { date: "", zone_id: "", sub_zone_id: "", manager_id: "", user_id: "" };

export default function OfficeDashboard() {
  const { user, visibility, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [subZones, setSubZones] = useState([]);
  const [managers, setManagers] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [visibleUsers, setVisibleUsers] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [kpis, setKpis] = useState(null);
  const [charts, setCharts] = useState(null);
  const [fieldForce, setFieldForce] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");

  // One-time setup: identity + filter-picker option lists.
  useEffect(() => {
    let mounted = true;
    (async () => {
      setBusy(true);
      setError("");
      try {
        if (!visibility) await refreshMe();
        const [uz, zres, ures] = await Promise.all([
          api.get("/users", { params: { page: 1, page_size: 1 } }),
          api.get("/zones"),
          api.get("/users", { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
        ]);
        if (!mounted) return;
        setVisibleUsers(uz.data.total);
        setZones(zres.data.items);
        const users = ures.data.items || [];
        setManagers(users.filter((u) => MANAGER_ROLES.includes(u.role_code)));
      } catch (e) {
        if (mounted) setError(formatApiError(e));
      } finally {
        if (mounted) setBusy(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sub-zones cascade with the Zone filter.
  useEffect(() => {
    if (!filters.zone_id) { setSubZones([]); return; }
    let mounted = true;
    api.get("/geography/sub-zones", { params: { zone_id: filters.zone_id } })
      .then((r) => mounted && setSubZones(r.data.items || []))
      .catch(() => mounted && setSubZones([]));
    return () => { mounted = false; };
  }, [filters.zone_id]);

  // "User" filter options narrow to whichever of Manager/Zone/Sub-zone is
  // already picked — selecting a Manager only offers their own reporting
  // subtree, not every user in the system.
  useEffect(() => {
    let mounted = true;
    const params = {};
    if (filters.manager_id) params.manager_id = filters.manager_id;
    if (filters.zone_id) params.zone_id = filters.zone_id;
    if (filters.sub_zone_id) params.sub_zone_id = filters.sub_zone_id;
    api.get("/dashboard/team-users", { params })
      .then((r) => {
        if (!mounted) return;
        const items = r.data.items || [];
        setTeamUsers(items);
        // Drop a previously-picked User if they've fallen outside the new scope.
        if (filters.user_id && !items.some((u) => u.id === filters.user_id)) {
          setFilters((f) => ({ ...f, user_id: "" }));
        }
      })
      .catch(() => mounted && setTeamUsers([]));
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.manager_id, filters.zone_id, filters.sub_zone_id]);

  // Business-KPI widgets — refetch whenever a filter changes. Each call is
  // independent; a 403 on one (e.g. Alerts needs a separate workflow
  // permission) must not blank the rest.
  useEffect(() => {
    let mounted = true;
    const params = {};
    if (filters.date) params.date = filters.date;
    if (filters.zone_id) params.zone_id = filters.zone_id;
    if (filters.sub_zone_id) params.sub_zone_id = filters.sub_zone_id;
    if (filters.manager_id) params.manager_id = filters.manager_id;
    if (filters.user_id) params.user_id = filters.user_id;

    api.get("/dashboard/kpis", { params }).then((r) => mounted && setKpis(r.data)).catch(() => mounted && setKpis(null));
    api.get("/dashboard/charts", { params: { ...params, days: 14 } })
      .then((r) => mounted && setCharts(r.data)).catch(() => mounted && setCharts(null));
    api.get("/dashboard/field-force", { params })
      .then((r) => mounted && setFieldForce(r.data)).catch(() => mounted && setFieldForce(null));
    api.get("/dashboard/alerts", { params }).then((r) => mounted && setAlerts(r.data)).catch(() => mounted && setAlerts(null));
    return () => { mounted = false; };
  }, [filters]);

  const myZones = (zones || []).filter((z) => user?.assigned_zone_ids?.includes(z.id));
  const canGlobal = visibility?.is_global;
  const trend = charts?.sales_trend || [];
  const subZoneBar = (charts?.sub_zone_bar || []).slice(0, 10);
  const topPerformers = charts?.top5 || [];
  // Was capped to 8 regardless of how many field users were actually in
  // scope — invisible with a narrow Manager filter (few people anyway) but
  // glaring with "All managers" selected, where it silently hid everyone
  // past the 8th row. The table scrolls instead of truncating now.
  const ffRows = fieldForce?.items || [];
  const filtersActive = Object.values(filters).some(Boolean);

  return (
    <div className="space-y-6" data-testid="office-dashboard">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-neutral-400 mb-1">Dashboard</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-neutral-900">
          Hello, <span className="tulsi-text-gradient">{user?.name?.split(" ")[0]}</span>
        </h1>
        <p className="text-sm text-neutral-500 mt-1">
          Here's how {kpis?.date || "…"} is shaping up{filters.zone_id || filters.sub_zone_id || filters.manager_id || filters.user_id ? " (filtered)" : ""}.
        </p>
      </div>

      <FavoritesRow />

      {/* Filter bar — Date / Zone / Sub-Zone / Manager / User. Narrows every
          widget below (KPIs, charts, field-force, exceptions) to the picked
          scope; server enforces this can only narrow, never escalate,
          beyond the caller's own visibility. */}
      <Card className="border-neutral-200 rounded-2xl" data-testid="dashboard-filter-bar">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
            <div>
              <Label className="text-[11px]">Date</Label>
              <Input type="date" className="h-9" value={filters.date}
                     data-testid="dash-filter-date"
                     onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value }))} />
            </div>
            <div>
              <Label className="text-[11px]">Zone</Label>
              <Select value={filters.zone_id || "all"}
                      onValueChange={(v) => setFilters((f) => ({ ...f, zone_id: v === "all" ? "" : v, sub_zone_id: "" }))}>
                <SelectTrigger className="h-9" data-testid="dash-filter-zone"><SelectValue placeholder="All zones" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All zones</SelectItem>
                  {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.code} — {z.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Sub-Zone</Label>
              <Select value={filters.sub_zone_id || "all"} disabled={!filters.zone_id}
                      onValueChange={(v) => setFilters((f) => ({ ...f, sub_zone_id: v === "all" ? "" : v }))}>
                <SelectTrigger className="h-9" data-testid="dash-filter-subzone">
                  <SelectValue placeholder={filters.zone_id ? "All sub-zones" : "Pick zone first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sub-zones</SelectItem>
                  {subZones.map((sz) => <SelectItem key={sz.id} value={sz.id}>{sz.code} — {sz.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Manager</Label>
              <SearchableSelect
                value={filters.manager_id}
                onChange={(v) => setFilters((f) => ({ ...f, manager_id: v }))}
                options={managers.map((m) => ({ id: m.id, code: m.role_code, name: m.name,
                                                  label: `${m.name} (${m.role_code})` }))}
                placeholder="All managers"
                testid="dash-filter-manager"
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-[11px]">User</Label>
              <SearchableSelect
                value={filters.user_id}
                onChange={(v) => setFilters((f) => ({ ...f, user_id: v }))}
                options={teamUsers.map((u) => ({ id: u.id, code: u.role_code, name: u.name,
                                                  label: `${u.name} (${u.role_code})` }))}
                placeholder="All users"
                testid="dash-filter-user"
                className="h-9"
              />
            </div>
            <div>
              <Button variant="outline" className="h-9 w-full" disabled={!filtersActive}
                      onClick={() => setFilters(EMPTY_FILTERS)} data-testid="dash-filter-clear">
                Clear filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div
          data-testid="dashboard-error"
          className="text-sm rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2"
        >
          {error}
        </div>
      )}

      {/* Today's business KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          testid="stat-sales-today"
          icon={Weight}
          label="Kg sold today"
          value={kpis ? KG(kpis.total_kg_sold) : busy ? "…" : KG(0)}
          tint="linear-gradient(135deg,#E5197F,#6D2C91)"
          onClick={() => navigate("/reports/order")}
        />
        <Stat
          testid="stat-orders-today"
          icon={ShoppingCart}
          label="Orders today"
          value={kpis ? kpis.orders_count : busy ? "…" : 0}
          tint="#6D2C91"
          onClick={() => navigate("/admin/orders")}
        />
        <Stat
          testid="stat-field-active"
          icon={CalendarCheck}
          label="Field users checked-in"
          value={kpis ? kpis.active_field_users : busy ? "…" : 0}
          sub={kpis ? `Visit coverage: ${kpis.coverage_pct}%` : undefined}
          tint="#E5197F"
        />
        <Stat
          testid="stat-pending-approvals"
          icon={ClipboardList}
          label="Pending approvals"
          value={kpis ? kpis.pending_approvals : busy ? "…" : 0}
          tint="linear-gradient(135deg,#6D2C91,#3A1A5F)"
          onClick={() => navigate("/approvals/inbox")}
        />
      </div>

      {/* Today's exceptions — hidden entirely if the caller lacks the
          extra workflow permission this endpoint requires (403 → null). */}
      {alerts && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat testid="stat-absentees" icon={UserX} label="Absent today"
                value={alerts.absentees} tint="#B91C1C" />
          <Stat testid="stat-zero-order" icon={Ban} label="Zero-order field staff"
                value={alerts.zero_order_field} tint="#D97706" />
          <Stat testid="stat-missed-beats" icon={AlertTriangle} label="Missed beats"
                value={alerts.missed_beats} tint="#B45309" />
          <Stat testid="stat-aging-approvals" icon={Clock}
                label={`Approvals aging >${alerts.aging_threshold_days}d`}
                value={alerts.aging_approvals} tint="#7C2D12"
                onClick={() => navigate("/approvals/inbox")} />
        </div>
      )}

      {/* Kg sold trend */}
      <SectionCard title="Kg sold trend (last 14 days)" testid="dash-sales-trend">
        {!charts ? (
          <div className="flex items-center gap-2 text-neutral-500 text-sm py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : trend.length === 0 ? (
          <div className="text-sm text-neutral-500 py-8 text-center">No orders in this window yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trend} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E5197F" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#E5197F" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }}
                     tickFormatter={(d) => d?.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v)}`} />
              <Tooltip formatter={(v, n) => [n === "qty_kg" ? KG(v) : v, n === "qty_kg" ? "Kg sold" : "Orders"]}
                       labelFormatter={(d) => d} />
              <Area type="monotone" dataKey="qty_kg" stroke="#E5197F" fill="url(#salesGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Sub-zone-wise Kg sold (last 14 days)" testid="dash-zone-bar">
          {!charts ? (
            <div className="flex items-center gap-2 text-neutral-500 text-sm py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : subZoneBar.length === 0 ? (
            <div className="text-sm text-neutral-500 py-8 text-center">No sub-zone data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={subZoneBar} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="sub_zone_code" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v)}`} />
                <Tooltip formatter={(v) => KG(v)}
                         labelFormatter={(_, p) => p?.[0]?.payload?.sub_zone_name || ""} />
                <Bar dataKey="qty_kg" fill="#6D2C91" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        <SectionCard title="Top performers this month" testid="dash-top-performers">
          {!charts ? (
            <div className="flex items-center gap-2 text-neutral-500 text-sm py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : topPerformers.length === 0 ? (
            <div className="text-sm text-neutral-500 py-8 text-center">No orders this month yet.</div>
          ) : (
            <div className="space-y-2">
              {topPerformers.map((p, i) => (
                <div key={p.user_id || i}
                     className="flex items-center justify-between rounded-xl border border-neutral-100 px-3 py-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-7 w-7 rounded-full bg-[#6D2C91]/10 text-[#6D2C91] text-xs font-bold
                                    flex items-center justify-center shrink-0">
                      {i + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-neutral-900 truncate">
                        {p.user_name || "—"}
                      </div>
                      <div className="text-[11px] text-neutral-400">{p.role_code} · {p.orders} orders</div>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-neutral-900 shrink-0">{KG(p.qty_kg)}</div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Field force today */}
      <SectionCard title="Field force — today"
                   action={<TrendingUp className="h-4 w-4 text-neutral-400" />}
                   testid="dash-field-force">
        {!fieldForce ? (
          <div className="flex items-center gap-2 text-neutral-500 text-sm py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : ffRows.length === 0 ? (
          <div className="text-sm text-neutral-500 py-8 text-center">No field users in scope.</div>
        ) : (
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-[11px] uppercase tracking-wide text-neutral-400 border-b border-neutral-100">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Role</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Visits</th>
                  <th className="py-2 pr-3">Orders</th>
                  <th className="py-2 pr-3">Kg sold</th>
                </tr>
              </thead>
              <tbody>
                {ffRows.map((r) => (
                  <tr key={r.user_id} className="border-b border-neutral-50 last:border-0">
                    <td className="py-2 pr-3 font-medium text-neutral-900">{r.user_name}</td>
                    <td className="py-2 pr-3 text-neutral-500">{r.role_code}</td>
                    <td className="py-2 pr-3">
                      <span className={"px-2 py-0.5 rounded-full text-[11px] font-medium "
                        + (r.checked_in ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                        : "bg-red-50 text-red-700 border border-red-200")}>
                        {r.checked_in ? "Checked in" : "Not checked in"}
                      </span>
                    </td>
                    <td className="py-2 pr-3">{r.visits_done}</td>
                    <td className="py-2 pr-3">{r.orders_count}</td>
                    <td className="py-2 pr-3 font-semibold">{KG(r.orders_kg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {fieldForce.total > ffRows.length && (
              <div className="text-[11px] text-neutral-400 mt-2">
                Showing {ffRows.length} of {fieldForce.total} field users in scope.
              </div>
            )}
          </div>
        )}
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-neutral-200 rounded-2xl lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-neutral-800">
              Your zones
            </CardTitle>
          </CardHeader>
          <CardContent>
            {busy ? (
              <div className="flex items-center gap-2 text-neutral-500 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : canGlobal ? (
              <div className="flex flex-wrap gap-2">
                {zones.map((z) => (
                  <span
                    key={z.id}
                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-[#6D2C91]/10 text-[#6D2C91] border border-[#6D2C91]/20"
                  >
                    {z.name}
                  </span>
                ))}
                <span className="text-xs text-neutral-500">
                  Global access — every zone is visible.
                </span>
              </div>
            ) : myZones.length ? (
              <div className="flex flex-wrap gap-2">
                {myZones.map((z) => (
                  <span
                    key={z.id}
                    data-testid={`zone-chip-${z.code}`}
                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-[#E5197F]/10 text-[#E5197F] border border-[#E5197F]/20"
                  >
                    {z.name}
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-sm text-neutral-500">No zones assigned yet.</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-neutral-200 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-neutral-800">
              Quick actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              variant="outline"
              onClick={() => navigate("/change-password")}
              data-testid="quick-change-password"
              className="w-full justify-start border-neutral-200"
            >
              Change password
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/users")}
              data-testid="quick-users"
              className="w-full justify-start border-neutral-200"
              disabled={!user?.permissions?.includes("users.manage")}
            >
              User management
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/reports")}
              data-testid="quick-reports"
              className="w-full justify-start border-neutral-200"
            >
              All reports
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Compact identity strip — role/scope/visible-users, de-emphasised
          since the business KPIs above are the primary content now. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-neutral-400 pt-1">
        <span className="inline-flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" /> {busy ? "…" : visibleUsers ?? 0} users visible
        </span>
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" /> {user?.role_code?.replace(/_/g, " ") || "—"}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5" /> {canGlobal ? "All zones" : `${myZones.length} zone(s)`}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5" /> {canGlobal ? "Global" : "Hierarchy"} scope
        </span>
        {kpis?.generated_at && (
          <span className="ml-auto">Updated {new Date(kpis.generated_at).toLocaleTimeString()}</span>
        )}
      </div>
    </div>
  );
}
